import Box from "@/common/components/Box";
import { ShowHeader } from "@/common/components/ShowHeader";
import {
  BriefcaseBusinessIcon,
  Building2Icon,
  CalendarDaysIcon,
} from "lucide-react";
import NegotiatingProductsShow from "../../components/Show/NegotiatingProducts";
import ResumeBox from "@/common/components/ResumeBox";
import useNegotiation from "../../hooks/useNegotiation";
import { useParams } from "react-router-dom";
import { negotiationStatus } from "../../constants/negotiationStatus.constant";
import NegotiationsShowLoadingState from "./LoadingState";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import EquipmentCategorySummary from "../../components/EquipmentCategorySummary";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import { formatDate } from "@/common/utils/formatters";
import ButtonLink from "@/common/components/ButtonLink";

const NegotiationsShow = () => {
  const { id } = useParams();

  const {
    data: negotiation,
    isPending: negotiationIsPending,
    refetch: negotiationRefetch,
    error: negotiationError = {} as any,
  } = useNegotiation(id);

  if (negotiationIsPending) {
    return <NegotiationsShowLoadingState />;
  }

  return (
    <div>
      <div className="flex">
        <ShowHeader
          backTo="/negotiations"
          titleClassName="text-nowrap font-medium text-lg"
          title="Negociações"
          subtitle={negotiation?.number}
          className="w-full"
        />
      </div>

      <ErrorMessageWrapper
        refetchFunction={negotiationRefetch}
        statusCode={negotiationError?.status}
        className="h-[calc(100vh-128px)]"
      >
        <Box className="flex gap-6 h-[calc(100vh-128px)] overflow-y-auto">
          <div className="w-1/2 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <ResumeBox
                boxClassName="w-full"
                label="Empresa"
                value={
                  <ButtonLink
                    linkTo={`/companies/${negotiation?.client_company?.id}`}
                    operationKlassToCheckPermission="Admin::Client::Companies::Show"
                    hoverBackgroundSize="medium"
                    className="mt-1"
                  >
                    <ImageWithDisplayName
                      placeholderIcon={BriefcaseBusinessIcon}
                      imageUrl={
                        negotiation?.client_company?.general_information
                          ?.company_logo?.url
                      }
                      displayName={
                        negotiation?.client_company?.general_information
                          ?.fantasy_name
                      }
                      displayNameClassName="line-clamp-1 text-start"
                    />
                  </ButtonLink>
                }
              />

              <ResumeBox
                boxClassName="w-full"
                label="Estabelecimento"
                value={
                  <ButtonLink
                    linkTo={`/establishments/${negotiation?.establishment_company?.id}`}
                    operationKlassToCheckPermission="Admin::Establishment::Companies::Show"
                    hoverBackgroundSize="medium"
                    className="mt-1"
                  >
                    <ImageWithDisplayName
                      placeholderIcon={Building2Icon}
                      imageUrl={
                        negotiation?.establishment_company?.general_information
                          ?.company_logo?.url
                      }
                      displayName={
                        negotiation?.establishment_company?.general_information
                          ?.fantasy_name
                      }
                      displayNameClassName="line-clamp-1 text-start"
                    />
                  </ButtonLink>
                }
              />
            </div>

            <ResumeBox
              label="Status"
              value={
                negotiation?.status
                  ? negotiationStatus[negotiation?.status]
                  : ""
              }
            />

            <div className="flex flex-col gap-1">
              <p className="text-xs">Proposta da empresa</p>

              <Box className="p-4 text-sm bg-white-200 rounded-md max-h-[280px] overflow-y-auto">
                {negotiation?.observation}
              </Box>
            </div>

            <div className="flex flex-col gap-2">
              {["accepted", "expired"].includes(negotiation?.status || "") &&
                negotiation?.due_at && (
                  <ResumeBox
                    label="Validade da negociação"
                    value={
                      <div className="flex items-center gap-1">
                        <CalendarDaysIcon strokeWidth={1.5} size={20} />

                        <p>{formatDate(new Date(negotiation?.due_at))}</p>
                      </div>
                    }
                  />
                )}

              <EquipmentCategorySummary />
            </div>
          </div>

          <div className="w-1/2 flex flex-col gap-4">
            <NegotiatingProductsShow
              clientCompany={negotiation?.client_company}
              establishment={negotiation?.establishment_company}
              negotiationProducts={negotiation?.negotiation_products}
            />
          </div>
        </Box>
      </ErrorMessageWrapper>
    </div>
  );
};

export default NegotiationsShow;
